import { useState, useEffect } from 'react'
import { useAuthStore } from '@/store/authStore'
import { Space, Booking } from '@/types'
import { db } from '@/config/firebase'
import { collection, query, where, getDocs } from 'firebase/firestore'
import { Users, Search, MapPin, Calendar, ArrowRight, LayoutGrid, Ban } from 'lucide-react'
import { SPACE_TEMPLATES } from '@/utils/spaceTemplates'
import { Link } from 'react-router-dom'
import StarRating from '@/components/reviews/StarRating'

export default function BookingCatalog() {
    const { currentOrg } = useAuthStore()
    const [spaces, setSpaces] = useState<Space[]>([])
    const [loading, setLoading] = useState(true)
    const [searchTerm, setSearchTerm] = useState('')
    const [reservedSpaceIds, setReservedSpaceIds] = useState<Set<string>>(new Set())

    useEffect(() => {
        if (!currentOrg) return

        const loadSpaces = async () => {
            try {
                // Carregar espaços
                const q = query(collection(db, 'spaces'), where('orgId', '==', currentOrg.id))
                const snapshot = await getDocs(q)
                const loadedSpaces = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Space))
                setSpaces(loadedSpaces)

                // Carregar reservas ativas para verificar disponibilidade
                const now = new Date()
                const bookingsQuery = query(
                    collection(db, 'bookings'),
                    where('orgId', '==', currentOrg.id),
                    where('status', 'in', ['CONFIRMED', 'PENDING_APPROVAL', 'IN_PROGRESS', 'PENDING_PAYMENT'])
                )
                const bookingsSnapshot = await getDocs(bookingsQuery)
                const activeBookings = bookingsSnapshot.docs.map(doc => doc.data() as Booking)

                // Identificar espaços reservados (com reservas ativas)
                const reserved = new Set<string>()
                activeBookings.forEach(booking => {
                    const endDate = booking.end?.toDate?.() || new Date(0)
                    if (endDate > now) {
                        reserved.add(booking.spaceId)
                    }
                })
                setReservedSpaceIds(reserved)

            } catch (error) {
                console.error('Erro ao carregar espaços:', error)
            } finally {
                setLoading(false)
            }
        }

        loadSpaces()
    }, [currentOrg?.id])

    const filteredSpaces = spaces.filter(s =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.type.toLowerCase().includes(searchTerm.toLowerCase())
    )

    if (loading) {
        return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[1, 2, 3].map(n => (
                    <div key={n} className="h-80 bg-[#001c28]/5 rounded-[2.5rem] animate-pulse" />
                ))}
            </div>
        )
    }

    return (
        <div className="w-full mx-auto p-0 space-y-4 page-enter">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white p-4 rounded-[2rem] border border-[#001c28]/5 shadow-sm mb-8">
                <div className="flex items-center gap-4 px-2">
                    <div className="w-10 h-10 rounded-full bg-[#001c28]/5 flex items-center justify-center text-[#001c28]">
                        <LayoutGrid className="w-5 h-5" />
                    </div>
                    <div>
                        <p className="text-xs font-black text-[#001c28] uppercase tracking-widest">Todos os Espaços</p>
                        <p className="text-[10px] text-[#001c28]/50 font-medium">Explore as opções disponíveis em {currentOrg?.name}</p>
                    </div>
                </div>

                <div className="relative group w-full md:w-auto">
                    <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#001c28]/30 group-focus-within:text-[#d4a94d] transition-colors" />
                    <input
                        type="text"
                        placeholder="Buscar espaço..."
                        className="w-full md:w-72 bg-[#001c28]/[0.02] border border-[#001c28]/10 rounded-2xl pl-12 pr-4 py-3 text-xs font-medium focus:bg-white focus:ring-2 focus:ring-[#d4a94d]/20 focus:border-[#d4a94d] text-[#001c28] transition-all outline-none placeholder:text-[#001c28]/30 placeholder:uppercase placeholder:text-[10px] placeholder:tracking-wider placeholder:font-bold"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>
            </div>

            {filteredSpaces.length === 0 ? (
                <div className="bg-white rounded-[2.5rem] py-20 text-center border border-[#001c28]/5 shadow-sm">
                    <div className="w-20 h-20 bg-[#001c28]/5 rounded-3xl flex items-center justify-center mx-auto mb-6">
                        <MapPin className="w-8 h-8 text-[#001c28]/20" />
                    </div>
                    <h3 className="text-2xl font-black text-[#001c28] uppercase italic">Nenhum espaço disponível</h3>
                    <p className="text-[#001c28]/50 font-medium mt-2 text-sm">Tente buscar por outro termo ou volte mais tarde.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredSpaces.map(space => {
                        const template = SPACE_TEMPLATES[space.type] || SPACE_TEMPLATES.room
                        const Icon = template.icon
                        const isReserved = reservedSpaceIds.has(space.id)

                        return (
                            <div key={space.id} className={`bg-white rounded-[2.5rem] p-0 group hover:-translate-y-2 transition-all duration-500 overflow-hidden relative border border-[#001c28]/5 hover:border-[#d4a94d]/30 shadow-sm hover:shadow-2xl hover:shadow-[#001c28]/10 ${isReserved ? 'opacity-75' : ''}`}>
                                <div className="absolute -right-4 -top-4 w-28 h-28 bg-[#d4a94d]/10 rounded-full blur-2xl group-hover:bg-[#d4a94d]/20 transition-colors" />

                                {/* Tarja Diagonal RESERVADO */}
                                {isReserved && (
                                    <div className="absolute inset-0 z-20 pointer-events-none">
                                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rotate-[-35deg] w-[150%] bg-red-600 py-4 shadow-2xl">
                                            <div className="flex items-center justify-center gap-3">
                                                <Ban className="w-6 h-6 text-white" />
                                                <span className="text-white font-black text-2xl uppercase tracking-[0.3em] drop-shadow-lg">
                                                    RESERVADO
                                                </span>
                                                <Ban className="w-6 h-6 text-white" />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="relative z-10">
                                    <div className="aspect-[4/3] relative overflow-hidden bg-[#001c28]/5 rounded-t-[2.5rem]">
                                        {space.photos?.[0] ? (
                                            <img src={space.photos[0]} alt={space.name} className={`w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ${isReserved ? 'grayscale' : ''}`} />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-[#001c28]/10">
                                                <Icon className="w-20 h-20" />
                                            </div>
                                        )}
                                        <div className="absolute top-4 left-4">
                                            <span className="bg-white/95 backdrop-blur-md text-[#001c28] text-[10px] font-black px-4 py-2 rounded-xl uppercase tracking-widest flex items-center shadow-lg border border-[#001c28]/5">
                                                <Icon className="w-3 h-3 mr-2 text-[#d4a94d]" />
                                                {template.name}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="p-6">
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-xl font-black text-[#001c28] uppercase tracking-tight group-hover:text-[#d4a94d] transition-colors truncate pr-4">{space.name}</h3>
                                            <div className="flex items-center bg-[#001c28]/5 px-3 py-1.5 rounded-xl text-[#001c28]/80 text-[10px] font-black border border-[#001c28]/10">
                                                <Users className="w-3 h-3 mr-1.5" />
                                                {space.capacity}
                                            </div>
                                        </div>

                                        {/* Rating Section */}
                                        <div className="flex items-center gap-2 mb-4">
                                            {space.rating ? (
                                                <>
                                                    <StarRating rating={space.rating} readOnly size={14} />
                                                    <span className="text-xs font-bold text-[#001c28]/70">
                                                        {space.rating.toFixed(1)} ({space.totalReviews})
                                                    </span>
                                                </>
                                            ) : (
                                                <span className="text-[10px] font-bold text-[#d4a94d] bg-[#d4a94d]/10 px-2 py-1 rounded-lg uppercase tracking-wider border border-[#d4a94d]/20">
                                                    Novo
                                                </span>
                                            )}
                                        </div>

                                        <div className="flex flex-wrap gap-2 mb-6 min-h-[3rem]">
                                            {space.amenities.slice(0, 4).map((a, i) => (
                                                <span key={i} className="text-[9px] font-bold bg-[#001c28]/5 text-[#001c28]/60 px-2.5 py-1 rounded-lg uppercase tracking-wider border border-[#001c28]/5">
                                                    {a}
                                                </span>
                                            ))}
                                        </div>

                                        {isReserved ? (
                                            <button
                                                disabled
                                                className="w-full py-3 bg-gray-300 text-gray-500 text-xs font-black rounded-xl uppercase tracking-widest shadow-lg flex items-center justify-center space-x-3 cursor-not-allowed"
                                            >
                                                <Ban className="w-4 h-4" />
                                                <span>Indisponível</span>
                                            </button>
                                        ) : (
                                            <Link
                                                to={`/bookings/new/${space.id}`}
                                                className="w-full py-3 bg-[#001c28] group-hover:bg-[#d4a94d] text-white group-hover:text-[#001c28] text-xs font-black rounded-xl transition-all uppercase tracking-widest shadow-lg shadow-[#001c28]/20 flex items-center justify-center space-x-3"
                                            >
                                                <Calendar className="w-4 h-4" />
                                                <span>Reservar Agora</span>
                                                <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                                            </Link>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
